/**
 * PathwaysHub — the `/pathways` root page. Editorial index into the
 * two admission-route explainers we've shipped so far
 * (foundation-year, mature-learner) plus placeholder tiles for the
 * routes still on the roadmap. Central spot to link families who
 * arrived expecting a hub, not a specific pathway.
 */
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Users, Sparkles, Clock } from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

export default function PathwaysHub() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="pathways-hub">
      <PathwaySEO
        title="Admission pathways — the routes into a UK degree past the direct-entry front door"
        description="Foundation year, mature-learner routes, Access to HE, HND top-ups, degree apprenticeships. Every established alternative route into a UK bachelor's when the direct-entry offer isn't the right fit."
        path="/pathways"
      />

      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Admission pathways
        </div>
        <h1
          data-testid="pathways-hub-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          The routes past the direct-entry front door
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed text-lg">
          Not every family lines up at the same UCAS turnstile. Some
          missed a grade. Some are 34 and coming back to study. Some
          are switching careers with an HND already in hand. Every one
          of these has a real, well-established, government-recognised
          route into a full UK bachelor's. Pick the one that fits.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <PathwayCard
          to="/pathways/foundation-year"
          testid="pathways-hub-foundation-year"
          icon={<GraduationCap className="h-4 w-4 text-emerald-700" />}
          badge="Live"
          title="Foundation-year route"
          body="Missed your A-level offer or your home qualifications don't map onto UCAS Tariff? A Year 0 attached to a full bachelor's fixes that — 210+ UK courses offer it. Same campus, same accommodation, no reapplication."
          accent="emerald"
        />
        <PathwayCard
          to="/pathways/mature-learner"
          testid="pathways-hub-mature-learner"
          icon={<Users className="h-4 w-4 text-[#4C1D95]" />}
          badge="Live"
          title="Coming back to study at 25+"
          body="Four established adult routes into a UK bachelor's: Access to HE Diploma, HND-to-degree top-up, part-time bachelor's, and employer-sponsored degree apprenticeships. All costed under standard student finance — no age cap."
          accent="violet"
        />
        <PathwayCard
          to="#"
          testid="pathways-hub-clearing"
          icon={<Clock className="h-4 w-4 text-stone-500" />}
          badge="Coming soon"
          title="Clearing playbook"
          body="How Clearing actually works in August each year, when to call, what to say, and how to spot inflated Clearing offers that won't be honoured. Publishes ahead of the next Clearing cycle."
          accent="muted"
          disabled
        />
        <PathwayCard
          to="#"
          testid="pathways-hub-international"
          icon={<Sparkles className="h-4 w-4 text-stone-500" />}
          badge="Coming soon"
          title="International applicant primer"
          body="Country-by-country map of how your home qualification maps onto UK / US / EU / AU direct-entry offers. Includes visa-cost estimator and cohort-size expectations."
          accent="muted"
          disabled
        />
      </div>

      <div className="mt-10 text-[13px] text-stone-500">
        Not sure which route applies to you? Start with the two live
        explainers above — they cover 90% of the "I can't do plain
        UCAS at 17" cases we see.
      </div>
    </div>
  );
}

function PathwayCard({ to, testid, icon, badge, title, body, accent, disabled }) {
  const accentStyles = {
    emerald: "border-emerald-200 bg-white hover:border-emerald-500",
    violet: "border-[#4C1D95]/20 bg-white hover:border-[#4C1D95]",
    muted: "border-dashed border-stone-300 bg-stone-50/40 cursor-not-allowed",
  };
  const badgeStyles = {
    emerald: "border-emerald-400 bg-emerald-50 text-emerald-900",
    violet: "border-[#4C1D95]/40 bg-[#F5F0FF] text-[#4C1D95]",
    muted: "border-stone-300 bg-white text-stone-500",
  };

  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">{icon}</div>
        <span
          className={
            "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-underground-sc tracking-wider " +
            badgeStyles[accent]
          }
        >
          {badge}
        </span>
      </div>
      <div className="mt-3 font-editorial text-xl text-[#002147]">{title}</div>
      <p className="mt-2 text-[13.5px] text-stone-700 leading-snug">{body}</p>
      {!disabled && (
        <div className="mt-4 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] flex items-center gap-1.5">
          Read the explainer <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </>
  );

  const cls = "block rounded-lg border p-5 transition-colors " + accentStyles[accent];

  return disabled ? (
    <div data-testid={testid} className={cls}>
      {inner}
    </div>
  ) : (
    <Link data-testid={testid} to={to} className={cls}>
      {inner}
    </Link>
  );
}
